import React, { useEffect, useRef } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledAboutSection = styled.section`
  max-width: 900px;

  .inner {
    display: grid;
    grid-template-columns: 3fr 2fr;
    grid-gap: 50px;

    @media (max-width: 768px) {
      display: block;
    }
  }
`;
const StyledText = styled.div`
  ul.skills-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(140px, 200px));
    grid-gap: 0 10px;
    padding: 0;
    margin: 0 0 24px 0;
    overflow: hidden;
    list-style: none;

    li {
      position: relative;
      margin-bottom: 10px;
      padding-left: 20px;
      font-family: var(--font-mono);
      font-size: var(--fz-xs);

      &:before {
        content: '▹';
        position: absolute;
        left: 0;
        color: var(--green);
        font-size: var(--fz-sm);
        line-height: 12px;
      }
    }
  }

  .skill-category {
    margin-top: 24px;

    &:first-of-type {
      margin-top: 16px;
    }

    h4 {
      margin: 0 0 6px 0;
      color: var(--green);
      font-family: var(--font-mono);
      font-size: var(--fz-xxs);
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    ul.skills-list {
      margin: 0;
    }
  }
`;
const StyledPic = styled.div`
  position: relative;
  max-width: 300px;

  @media (max-width: 768px) {
    margin: 50px auto 0;
    width: 70%;
  }

  .wrapper {
    ${({ theme }) => theme.mixins.boxShadow};
    display: block;
    position: relative;
    width: 100%;
    border-radius: var(--border-radius);
    background-color: var(--green);

    &:hover,
    &:focus {
      outline: 0;
      transform: translate(-4px, -4px);

      &:after {
        transform: translate(8px, 8px);
      }

      .img {
        filter: none;
        mix-blend-mode: normal;
      }
    }

    .img {
      position: relative;
      border-radius: var(--border-radius);
      mix-blend-mode: multiply;
      filter: grayscale(100%) contrast(1);
      transition: var(--transition);
    }

    &:before,
    &:after {
      content: '';
      display: block;
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: var(--border-radius);
      transition: var(--transition);
    }

    &:before {
      top: 0;
      left: 0;
      background-color: var(--navy);
      mix-blend-mode: screen;
    }

    &:after {
      border: 2px solid var(--green);
      top: 14px;
      left: 14px;
      z-index: -1;
    }
  }
`;

const About = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  const skillCategories = [
    {
      label: 'Backend Engineering',
      skills: ['Go', '.NET', 'NestJS', 'Laravel', 'Fiber', 'gRPC', 'GraphQL'],
    },
    {
      label: 'Architecture & Infra',
      skills: ['AWS', 'Docker', 'EDA', 'Redis', 'RabbitMQ', 'MQTT', 'GitHub Actions', 'FinOps'],
    },
    {
      label: 'Frontend Strategy',
      skills: ['Angular', 'React', 'Vue / Nuxt', 'Flutter', 'Ionic'],
    },
  ];

  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <h2 className="numbered-heading">Sobre mi</h2>

      <div className="inner">
        <StyledText>
          <div>
            <p>
              Diseño sistemas distribuidos que equilibran la agilidad técnica con la estabilidad
              corporativa. Mi enfoque profesional se basa en construir arquitecturas resilientes (EDA,
              Microservicios, BFF) que no solo escalan bajo demanda, sino que optimizan los costos
              operativos de la organización.
            </p>

            <p>
              A lo largo de 6 años, he transformado retos técnicos en soluciones de negocio: desde
              escalar sistemas logísticos en{' '}
              <a href="https://dafiti.com.co">Dafiti LATAM</a>, hasta liderar la
              modernización arquitectónica en entornos corporativos como{' '}
              <a href="https://www.grupocibernos.com">Grupo Cibernos</a> y{' '}
              <a href="https://shf.com.co">SHF</a>.
            </p>

            <p>Actualmente, mi trabajo se centra en tres pilares:</p>

            <ul style={{ margin: '8px 0 0 0', padding: '0 0 0 20px', listStyle: 'none' }}>
              <li
                style={{
                  position: 'relative',
                  marginBottom: '10px',
                  paddingLeft: '20px',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--fz-xs)',
                  color: 'var(--slate)',
                }}>
                <span
                  style={{
                    position: 'absolute',
                    left: 0,
                    color: 'var(--green)',
                    fontWeight: 'bold',
                  }}>
                  ▹
                </span>
                <strong style={{ color: 'var(--lightest-slate)' }}>
                  Technical Discovery:
                </strong>{' '}
                Identificar el problema real antes de escribir la primera línea de código.
              </li>
              <li
                style={{
                  position: 'relative',
                  marginBottom: '10px',
                  paddingLeft: '20px',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--fz-xs)',
                  color: 'var(--slate)',
                }}>
                <span
                  style={{
                    position: 'absolute',
                    left: 0,
                    color: 'var(--green)',
                    fontWeight: 'bold',
                  }}>
                  ▹
                </span>
                <strong style={{ color: 'var(--lightest-slate)' }}>
                  AI-Augmented Engineering:
                </strong>{' '}
                Implementar agentes de IA para maximizar la velocidad de entrega y la calidad bajo
                principios SOLID.
              </li>
              <li
                style={{
                  position: 'relative',
                  marginBottom: '10px',
                  paddingLeft: '20px',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--fz-xs)',
                  color: 'var(--slate)',
                }}>
                <span
                  style={{
                    position: 'absolute',
                    left: 0,
                    color: 'var(--green)',
                    fontWeight: 'bold',
                  }}>
                  ▹
                </span>
                <strong style={{ color: 'var(--lightest-slate)' }}>FinOps &amp; Performance:</strong>{' '}
                Reducir la latencia y los costos de infraestructura mediante decisiones de
                arquitectura informadas.
              </li>
            </ul>

            
          </div>

          {skillCategories.map((category, i) => (
            <div className="skill-category" key={i}>
              <h4>{category.label}</h4>
              <ul className="skills-list">
                {category.skills.map((skill, j) => (
                  <li key={j}>{skill}</li>
                ))}
              </ul>
            </div>
          ))}
        </StyledText>

        <StyledPic>
          <div className="wrapper">
            <StaticImage
              className="img"
              src="../../images/me.jpg"
              width={500}
              quality={95}
              formats={['AUTO', 'WEBP', 'AVIF']}
              alt="Headshot"
            />
          </div>
        </StyledPic>
      </div>
    </StyledAboutSection>
  );
};

export default About;
